import { Brush, Loader2, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AuthImg } from "@/components/studio/AuthImg";
import { confirmWithCost } from "@/lib/cost";
import { api, errMsg } from "@/lib/api";

const FRONT = ["contents", "before you begin", "introduction", "dedication", "front matter", "foreword", "preface", "how to use", "title page", "copyright", "publication details", "about the author", "acknowledg"];
export const isFront = (h) => FRONT.some((f) => (h || "").toLowerCase().includes(f));

const start = async (p, body, ensureSaved, onStarted) => {
  try {
    await ensureSaved();
    const r = await api.post(`/products/${p.id}/chapter-art`, body);
    toast.success(`Drawing ${r.data.total} illustration${r.data.total === 1 ? "" : "s"} — about a minute each, 3 at a time`);
    onStarted(r.data);
  } catch (e) {
    toast.error(errMsg(e));
  }
};

export const ChapterArtBar = ({ p, theme, ensureSaved, onStarted }) => {
  const job = p.chapter_art_job || {};
  const running = job.status === "running";
  const have = p.chapter_art || {};
  const missing = (p.sections || []).filter((s) => !isFront(s.heading) && !have[s.id]).length;
  const drawAll = async () => {
    if (!missing) return toast("Every chapter already has an illustration — redraw single chapters from their banner");
    if (await confirmWithCost(p.id, { kind: "chapter_art" }, `Draw ${missing} chapter illustration${missing === 1 ? "" : "s"}?`)) start(p, {}, ensureSaved, onStarted);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="chapter-art-bar">
      <Button onClick={drawAll} disabled={running} className="rounded-none gap-2" style={{ background: theme.dark }} data-testid="chapter-art-draw-all-btn">
        {running ? <Loader2 size={15} className="animate-spin" /> : <Brush size={15} />}
        {running ? `Drawing ${job.done || 0} of ${job.total}` : missing ? `Draw chapter illustrations (${missing})` : "All chapters illustrated"}
      </Button>
      {running && job.current && <span className="text-xs text-[#5C5255] italic" data-testid="chapter-art-current">Last finished: {job.current}</span>}
      {!running && job.errors?.length > 0 && <span className="text-xs text-[#9B2C2C]" data-testid="chapter-art-errors">{job.errors.length} couldn't be drawn — try redrawing them</span>}
      <span className="text-xs text-[#5C5255]">Line art recoloured to the category. Appears in PDF chapter openers, the toolkit and carousel slides after “Rebuild files”.</span>
    </div>
  );
};

export const ChapterArt = ({ p, s, theme, ensureSaved, onStarted, onChanged }) => {
  const path = (p.chapter_art || {})[s.id];
  const job = p.chapter_art_job || {};
  const pending = job.status === "running" && job.section_ids?.includes(s.id) && !job.finished?.includes(s.id);
  const remove = async () => {
    if (!window.confirm("Remove this illustration? Drawing a new one uses your OpenRouter credit again.")) return;
    await api.delete(`/products/${p.id}/chapter-art/${s.id}`);
    onChanged();
  };
  const redraw = async () => {
    if (await confirmWithCost(p.id, { kind: "chapter_art", count: 1 }, path ? "Draw a new illustration for this chapter? It replaces the current one." : "Draw an illustration for this chapter?")) start(p, { section_ids: [s.id] }, ensureSaved, onStarted);
  };
  if (pending)
    return <div className="w-40 h-40 shrink-0 flex items-center justify-center border text-xs italic" style={{ borderColor: theme.accent, color: theme.accent }} data-testid="chapter-art-pending">Drawing…</div>;
  if (!path)
    return (
      <button onClick={redraw} disabled={job.status === "running"} className="w-40 h-40 shrink-0 border border-dashed flex flex-col items-center justify-center gap-2 text-xs disabled:opacity-40" style={{ borderColor: theme.accent, color: theme.accent }} data-testid="chapter-art-draw-one-btn">
        <Brush size={18} /> Draw illustration
      </button>
    );
  return (
    <div className="shrink-0 group relative" data-testid="chapter-art">
      <AuthImg path={`/products/${p.id}/chapter-art/${s.id}.png`} version={`${path}-${theme.dark}-${theme.accent}`} alt="" className="w-40 h-40 object-cover border" testId="chapter-art-img" />
      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity bg-black/50">
        <button onClick={redraw} disabled={job.status === "running"} className="p-1.5 text-white disabled:opacity-40" aria-label="Redraw" data-testid="chapter-art-redraw-btn"><RefreshCw size={14} /></button>
        <button onClick={remove} className="p-1.5 text-white" aria-label="Remove" data-testid="chapter-art-remove-btn"><Trash2 size={14} /></button>
      </div>
    </div>
  );
};
